import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, firstValueFrom } from "rxjs";
import { AttachmentMetaResource } from "../models/attachment-meta-resource.model";
import { AttachmentResource } from "../models/attachment-resource.model";
import { Confidentiality } from "../models/confidentiality.model";
import { MessageResource } from "../models/message-resource.model";

@Injectable({ providedIn: "root" })
export class MessagesService {
    private httpClient = inject(HttpClient);

    public getMessage$(messageId: string): Observable<MessageResource> {
        return this.httpClient.get<MessageResource>(`/api/messages/${messageId}`);
    }

    public getAttachment$(messageId: string, attachmentId: string): Observable<AttachmentResource> {
        return this.httpClient.get<AttachmentResource>(`/api/messages/${messageId}/attachments/${attachmentId}`);
    }

    public createNewMessage(draftMessageResource: MessageResource): Promise<MessageResource> {
        return firstValueFrom(this.httpClient.put<MessageResource>("/api/messages/", draftMessageResource));
    }

    public updateMessage(message: MessageResource): Promise<MessageResource> {
        return firstValueFrom(this.httpClient.put<MessageResource>(`/api/messages/${message.id}`, message));
    }

    public sendMessage(messageId: string, messageData: MessageResource): Promise<void> {
        return firstValueFrom(this.httpClient.post<void>(`/api/messages/${messageId}/send`, messageData));
    }

    public deleteById(messageId: string): Promise<void> {
        return firstValueFrom(this.httpClient.delete<void>(`/api/messages/${messageId}`));
    }

    public copyMessage(messageId: string): Promise<MessageResource> {
        return firstValueFrom(this.httpClient.post<MessageResource>(`/api/messages/${messageId}/copy`, {}));
    }

    public uploadAttachment(messageId: string, fileToUpload: File, attachmentMeta?: AttachmentMetaResource): Promise<AttachmentResource> {
        const formData = new FormData();
        formData.append("file", fileToUpload, fileToUpload.name);

        const metadata = attachmentMeta || {
            documentType: null,
            confidentiality: Confidentiality.Normal,
            documentDate: new Date().toISOString(),
            documentDescription: "",
            convert: true,
            usePriorityQueue: true
        };

        formData.append("metadata", JSON.stringify(metadata));

        return firstValueFrom(this.httpClient.post<AttachmentResource>(`/api/messages/${messageId}/attachments`, formData));
    }

    public updateAttachment(messageId: string, attachmentId: string, updatedAttachment: AttachmentResource): Promise<void> {
        return firstValueFrom(this.httpClient.put<void>(`/api/messages/${messageId}/attachments/${attachmentId}`, updatedAttachment));
    }

    public deleteAttachmentById(messageId: string, attachmentId: string): Promise<void> {
        return firstValueFrom(this.httpClient.delete<void>(`/api/messages/${messageId}/attachments/${attachmentId}`));
    }

    public restoreAttachment(messageId: string, attachmentId: string): Promise<void> {
        return firstValueFrom(this.httpClient.put<void>(`/api/messages/${messageId}/attachments/${attachmentId}/restore`, {}));
    }
}
